/**
 * What is still missing from a discharge summary.
 *
 * Advisory, not blocking: this names gaps rather than guessing whether they
 * matter. A patient can legitimately be discharged on no medication, so an
 * empty take-home list is not a gap — an unnamed diagnosis is.
 *
 * Before this existed there was no completeness check anywhere in the wizard:
 * an empty summary compiled, previewed and finalized without a single
 * objection.
 */

const hasText = (v) => typeof v === "string" && v.trim() !== "";

const hasEntries = (v) =>
  Array.isArray(v) && v.some((x) => (typeof x === "string" ? x.trim() !== "" : Boolean(x)));

const CHECKS = [
  {
    key: "final_diagnosis",
    label: "Final diagnosis",
    test: (s) => hasEntries(s?.final_diagnosis),
  },
  {
    key: "presenting_complaints",
    label: "Presenting complaints",
    test: (s) => hasEntries(s?.history?.presenting_complaints),
  },
  {
    key: "condition_at_discharge",
    label: "Condition at discharge",
    test: (s) => hasText(s?.header?.discharge_details?.condition_at_discharge),
  },
  {
    key: "vital_signs",
    label: "Vital signs at discharge",
    test: (s) => {
      const v = s?.header?.discharge_details?.vital_signs || {};
      return ["bp", "pulse", "temperature", "spo2"].some((k) => hasText(String(v[k] ?? "")));
    },
  },
  {
    key: "follow_up",
    label: "Follow-up plan",
    test: (s) =>
      hasText(s?.advices?.follow_up?.department) ||
      hasText(String(s?.advices?.follow_up?.after_days ?? "")) ||
      hasText(s?.advices?.follow_up?.purpose),
  },
  { key: "prepared_by", label: "Prepared by", test: (s) => hasText(s?.prepared_by?.name) },
  { key: "checked_by", label: "Checked by", test: (s) => hasText(s?.checked_by?.name) },
];

export const unmetCompletenessItems = (summary) =>
  CHECKS.filter((c) => !c.test(summary || {})).map(({ key, label }) => ({ key, label }));

export default unmetCompletenessItems;
